import { useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector, useStore } from "react-redux";
import useClientConfig from "../hooks/clientConfigHook";
import { realize, useInit, useReadonly } from "../hooks/initHook";
import useNetwork from "../hooks/networkHook";
import { distinct, ensureArray } from "../utils/arrayUtilities";
import { CustomEqualityMap } from "../utils/customEqualityMap";
import { deepEquals } from "../utils/objectUtilities";
import useCredentials from "../hooks/credentialsHook";
import { useNetworkState } from "../hooks/appStateHook";
import { useTranslation } from "react-i18next";
import useWebsocket from "../hooks/websocketHook";
import { createSelector } from "@reduxjs/toolkit";

const NOP_INIT = { run: () => { }, listen: {} };

export const compareArray = (prev, curr) => {
    return prev.length === curr.length && prev.every((value, idx) => value === curr[idx]);
}

export const deepCompareArray = (prev, curr) => {
    return prev.length === curr.length && prev.every((value, idx) => deepEquals(value, curr[idx]));
}

export const compareSimpleMap = (prev, curr) => {
    return compareArray(Object.values(prev), Object.values(curr));
}

export const compareGroupedMap = (prev, curr) => {
    const prevValues = Object.values(prev);
    const currValues = Object.values(curr);
    if (prevValues.length !== currValues.length) {
        return false;
    }
    return prevValues.every((value, idx) => compareArray(value, currValues[idx]))
}

export const createMapSelector = (...props) => {
    const selector = createSelector(...props);
    selector.linear = data => ensureArray(Object.values(data).flat());
    return selector;
}

export const initMapFactory = (run, listen, { idProcessor, allowEmpty = false, isEmpty = id => id.every(i => i === undefined || i === null), name, authenticated = true, immortal, immutable } = {}) => {
    const initMap = new Map();
    return (...id) => {
        if (idProcessor) {
            id = idProcessor(...id);
            if (!Array.isArray(id)) {
                id = [id];
            }
        }
        if (!allowEmpty && isEmpty(id)) {
            return NOP_INIT;
        }
        const key = id.map(id => id?.toString()).join('-');
        if (!initMap.has(key)) {
            const runInstance = run(...id);
            runInstance.origin = name + '_' + key;
            runInstance.guid = crypto.randomUUID();
            initMap.set(key, { run: runInstance, listen, name: 'init_' + name + '_' + key, authenticated, immortal, immutable })
        }
        return initMap.get(key);
    }
}

/**
 * A meta-factory that is used to allow bulk selects / inits against a single select initMapFactory for a list of primary keys.
 * For example if you have an initMapFactory that takes the id of an entry and returns it, fetching if needed,
 * this meta-factory allows you to create a factory that can take an array of ids and init them all through
 * your single initMapFactory so that subsequent single id queries that overlap re-use the result
 * @param {*} factory an initMapFactory instance
 * @param {*} listen a function to call to lisen for changes / updates
 * @param {*} options options to control the multimap behaviour - key is the strict option. If clean-up is ENABLED for a given slice
 *            then the multimap MUST generate a new run function for each combination of arguments so that the data is refreshed as there
 *            is a risk of partial data removal by the cleanup which, without a new run function, risks the slice returning incomplete results
 *            when the slice data is immortal we can set strict false to increase run sharing and decrease fetching
 * @returns 
 */
export const initMultiMapFactory = (factory, listen, { strict = true, name, authenticated = true } = {}) => {
    const matcher = strict ?
        (mapKey, searchKey) => searchKey.length === mapKey.length && searchKey.every(e => mapKey.some(e2 => deepEquals(e, e2)))
        : (mapKey, searchKey) => searchKey.length <= mapKey.length && searchKey.every(e => mapKey.some(e2 => deepEquals(e, e2)))
    const initMap = new CustomEqualityMap(matcher);
    return ids => {
        if (initMap.has(ids)) {
            return initMap.get(ids);
        }
        if (ids.length === 0) {
            return NOP_INIT;
        }
        const entryInits = ids.map(id => Array.isArray(id) ? factory(...id) : factory(id));
        const init = {
            run: entryInits.map(({ run }) => run),
            listen,
            name,
            authenticated
        };
        initMap.set(ids, init)
        return init;
    }
}

/**
 * This network proxy is used in the unifyingMapFactory and queryMap Factory where we need to make sure
 * that we mark data elements as fetched before they are passed to the overall success action. This limited
 * proxy only supports actions that could return multiple data items (get/post). Each data item is processed
 * through the suppled processElement before the data set is passed to the successAction builder (if any)
 * and returned.
 * @param {*} network 
 * @param {*} processElement 
 * @returns 
 */
const networkProxy = (network, processElement) => {
    const successActionProxy = successAction => (data, meta, tools) => {
        ensureArray(data).forEach(entry => {
            processElement(entry)
        })
        return successAction?.(data, meta, tools);
    };
    return {
        get: (url, successAction, failureAction) => {

            return network.get(url, successActionProxy(successAction), failureAction);
        },
        put: () => { throw new Error('Cannot use a PUT operation in a query or unifying map factory') },
        post: (url, data, successAction, failureAction) => {
            return network.post(url, data, successActionProxy(successAction), failureAction);
        },
        delete: () => { throw new Error('Cannot use a DELETE operation in a query or unifying map factory') },
        emit: () => { throw new Error('Cannot use a EMIT operation in a query or unifying map factory') },
        dispatch: (...args) => network.dispatch(...args),
        postData: () => { throw new Error('Cannot use a postData operation in a query or unifying map factory') },
        fetchData: () => { throw new Error('Cannot use a fetchData operation in a query or unifying map factory') },
        dataURIToBlob: () => { throw new Error('Cannot use a dataURIToBlob operation in a query or unifying map factory') },
        downloadBlob: () => { throw new Error('Cannot use a downoadBlob operation in a query or unifying map factory') }
    };
}

/**
 * A meta-factory that is used handle initialization where multiple requests are unified into a single global fetch all.
 * For example if you have an initMapFactory that takes the id of an entry and returns it, fetching if needed,
 * this meta-factory allows you to create a factory that will ensure all ids are initialized with a single fetch and
 * any future overlapping single fetches re-use the global result.
 * @param {*} query query function returning data
 * @param {*} pkey the primary key for the returned data
 * @param {*} factory the underlying init factory
 * @param {*} listen a function to call to lisen for changes / updates
 * @param {*} options controlling the behaviour of the produced initializer
 * @returns 
 */
export const unifyingMapFactory = (query, pkey, factory, listen, { name, keyBuilder = (pkey, data) => ensureArray(pkey).map(key => data[key]), authenticated = true } = {}) => {
    const unifyingMapInstance = ({
        run: async (networkInput, processElement = () => { }) => {
            const network = networkProxy(networkInput, entry => {
                const entryRun = factory(...keyBuilder(pkey, entry))?.run;
                if (entryRun) {
                    processElement(entryRun);
                }
            });
            const data = await query(network);
            return data;
        },
        immortal: true,
        listen,
        name,
        authenticated
    })
    return () => unifyingMapInstance
}

/**
 * A meta-factory that is used handle initialization where the parameters to the query do not directly map to the ids of elements
 * initialized by the query - instead we post-process the returned data to know which elements have been initialized.
 * For example if you have an initMapFactory that takes the id of an entry and returns it, fetching if needed,
 * this meta-factory allows you to create a factory queries the entries by date or some other non-pkey property with a single fetch and
 * any future overlapping single fetches re-use the query result.
 * @param {*} query query function returning data
 * @param {*} pkey the primary key for the returned data
 * @param {*} factory the underlying init factory
 * @param {*} listen a function to call to lisen for changes / updates
 * @param {*} options controlling the behaviour of the produced initializer
 * @returns 
 */
export const queryMapFactory = (query, pkey, factory, listen, { allowEmpty = false, isEmpty = args => args.every(i => i === undefined || i === null), name, keyBuilder = (pkey, data, args) => ensureArray(pkey).map(key => data[key]), authenticated = true } = {}) => {
    const initMap = new CustomEqualityMap((mapKey, searchKey) => searchKey.length === mapKey.length && searchKey.every((e, i) => deepEquals(e, mapKey[i])));
    return (...args) => {
        if (initMap.has(args)) {
            return initMap.get(args);
        }
        if (!allowEmpty && isEmpty(args)) {
            return NOP_INIT;
        }
        const init = {
            run: async (networkInput, processElement) => {
                const network = networkProxy(networkInput, entry => {
                    const entryRun = factory(...keyBuilder(pkey, entry, args))?.run;
                    if (entryRun) {
                        processElement(entryRun);
                    }
                })
                const data = await (args?.length > 0 ? query(...args) : query)(network);
                return data;
            },
            listen,
            name,
            authenticated
        }
        initMap.set(args, init);
        return init;
    }
}

export const initSliceMapFactory = (query, pkey, factory, listen, { name, keyBuilder = (pkey, data, args) => ensureArray(pkey).map(key => data[key]), authenticated = true } = {}) => {
    const matcher = ({ start: mapStart, end: mapEnd, args: mapArgs }, { start: searchStart, end: searchEnd, args: searchArgs }) => mapStart <= searchEnd && searchStart <= mapEnd && deepEquals(mapArgs, searchArgs);
    const initMap = new CustomEqualityMap(matcher);
    return (start, end, ...args) => {
        const key = { start, end, args };
        if (initMap.has(key)) {
            return initMap.get(key);
        }
        if (start === end) {
            return NOP_INIT;
        }
        const existing = initMap.keys().filter(({ args: keyArgs }) => deepEquals(keyArgs, args));
        const ranges = [];
        if (existing.length === 0) {
            ranges.push(key);
        } else {
            if (start < existing[0].start) {
                ranges.push({ start: start, end: existing[0].end, args });
            }
            if (existing.length > 1) {
                for (let i = 0; i < existing.length - 1; ++i) {
                    ranges.push({ start: existing[i].end, end: existing[i + 1].start, args });
                }
            }
            if (end >= existing[existing.length - 1].end) {
                ranges.push({ start: existing[existing.length - 1].end, end, args });
            }
        }
        const init = {
            run: async (networkInput, processElement) => {
                const network = networkProxy(networkInput, entry => {
                    const entryRun = factory(...keyBuilder(pkey, entry, args))?.run
                    if (entryRun) {
                        processElement(entryRun);
                    }
                })
                let data = [];
                for (const range of ranges) {
                    data = data.concat(await query(range.start, range.end, args)(network));
                }
                return data;
            },
            listen,
            name,
            authenticated
        };
        initMap.set(key, init);
        return init;
    }
}

const cleanUpMap = new Map();
const setCleanupTimeout = window.requestIdleCallback
  ? (callback, delay) => {
      const timeoutId = setTimeout(() => {
        const idleId = requestIdleCallback(callback, { timeout: delay * 10 });
        // Store both timeout and idleCallback ID so either can be cleared
        cleanupIdMap.set(idleId, timeoutId);
      }, delay * 5);
      return timeoutId;
    }
  : (callback, delay) => setTimeout(callback, delay * 5);

const clearCleanupTimeout = (id) => {
  clearTimeout(id);
  for (const [idleId, timeoutId] of cleanupIdMap.entries()) {
    if (timeoutId === id) {
      cancelIdleCallback(idleId);
      cleanupIdMap.delete(idleId);
      break;
    }
  }
};

const cleanupIdMap = new Map();

class CleanupWork {
    static BATCH_SIZE = 500; // Adjustable batch size for removal

    store;
    #toClean;
    #cleanup;
    #dispatch;

    constructor({ dispatch, store, cleanup }) {
        this.#dispatch = dispatch;
        this.store = store;
        this.#cleanup = cleanup;
        this.#toClean = new Map(cleanup.all(store.getState()).map(entry => [cleanup.key?.(entry) ?? entry, entry]));
    }

    process(durationMs) {
        const start = performance.now();
        const key = this.#cleanup.key;

        // Process selectors within the time budget
        for (const sel of cleanUpMap.get(this.#cleanup.root)?.keys() || []) {
            const results = sel.linear ? sel.linear(sel(this.store.getState())) : ensureArray(sel(this.store.getState()));
            for (const entry of results) {
                this.#toClean.delete(key?.(entry) ?? entry);
            }
        }

        // Remove items in batches within the time budget
        while (this.#toClean.size > 0) {
            const batch = [];
            for (const [key, entry] of this.#toClean) {
                batch.push(entry);
                this.#toClean.delete(key);
                if (batch.length >= CleanupWork.BATCH_SIZE) break;
            }

            if (batch.length > 0) {
                if (this.#cleanup.removeAll) {
                    this.#dispatch(this.#cleanup.removeAll(batch));
                } else {
                    for (const entry of batch) {
                        this.#dispatch(this.#cleanup.remove(entry));
                    }
                }
            }

            if (performance.now() - start >= durationMs) {
                return true; // more work remains
            }
        }

        return false; // all work complete
    }
}

function runCleanup(work) {
    if (work?.process(250)) {
        setCleanupTimeout(() => runCleanup(work), 250);
    }
}
/**
 * A data hook which reads data from the redux store while also ensuring the data is fetch and kept fresh from the server using a specified run function.
 * @param {*} selector A standard redux selector for the data to be returned by the generated hook
 * @param {*} init An init object or generator; an init object has a run method (to fetch data) and a listen method (to install listeners) which returns a cleanup function
 * @param {*} options A collection of options to the hook factory. The cleanup field holds an object with root field holding a string that is the name of the group this clean-up belongs to,
 *  an all field with a function to get all state data items for this cleanup group, and a remove action that can be dispatched with an item that needs to be removed. The condition is a function
 *  which consumes the first n arguments passed to the resulting data hook to produce a boolean value that gates data fetch. The default value is the value to be returned by produced realize
 *  function is dependencies are not met.
 * @returns 
 */
export const dataHookFactory = (selector, init, { cleanup, condition, comparator, defaultValue = [] } = {}) => {
    if (init.immortal && cleanup) {
        throw new Error("It is never valid to supply a cleanup for an immortal init");
    }
    if (!init.immortal && !cleanup) {
        //console.warn("data hooks which are not immortal should have a cleanup");
    }
    if (init.listen && typeof init.listen !== 'object') {
        throw new Error("The listen must be an object with keys that are event names and values that are INLINE objects so their identities are unique");
    }
    return [(...args) => {
        const store = useStore();
        const dispatch = useDispatch();
        const { isOnline } = useNetworkState();
        const cleanupTimeout = useRef(null);
        const isOnlineGuard = useRef(isOnline);

        useEffect(() => {
            isOnlineGuard.current = isOnline;
        }, [isOnline]);

        const hookArgs = useMemo(() => condition ? args.slice(condition.length) : args, [args]);
        const deps = condition?.(...(args.slice(0, condition.length))) ?? true;

        const dataSelector = useMemo(() => hookArgs.length === 0 ? selector : selector(...hookArgs), [...hookArgs])
        const data = useSelector(dataSelector, comparator);

        const initFunction = useMemo(() => {
            const i = hookArgs.length === 0 || typeof init !== 'function' ? init : init(...hookArgs);
            let mergedDeps = deps;
            if (i.deps) {
                if (typeof i.deps === 'function') {
                    mergedDeps = (...args) => deps && i.deps(...args);
                } else if (typeof i.deps === 'boolean') {
                    mergedDeps = deps && i.deps;
                } else if (Array.isArray(i.deps)) {
                    mergedDeps = [...i.deps, deps];
                } else {
                    throw new Error("Don't know how to merge deps for type " + typeof i.deps);
                }
            }
            return { ...i, deps: mergedDeps };
        }, [...hookArgs, deps]);
        useInit(initFunction, !cleanup);

        useEffect(() => {
            // run clean-up if it has been opeted into
            if (cleanup) {
                // register so we have all the selectors for the same root ready for cleanup
                if (!cleanUpMap.has(cleanup.root)) {
                    cleanUpMap.set(cleanup.root, new Map([[dataSelector, 1]]));
                } else {
                    const root = cleanUpMap.get(cleanup.root);
                    if (root.has(dataSelector)) {
                        root.set(dataSelector, root.get(dataSelector) + 1);
                    } else {
                        root.set(dataSelector, 1);
                    }
                }

                //do cleanup of unused data
                return () => {
                    const root = cleanUpMap.get(cleanup.root);

                    if (root && isOnlineGuard.current) {
                        if (root.has(dataSelector)) {
                            if (root.get(dataSelector) === 1) {
                                root.delete(dataSelector);
                            } else {
                                root.set(dataSelector, root.get(dataSelector) - 1);
                            }
                        }
                        if (root.size === 0) {
                            cleanUpMap.delete(cleanup.root);
                        }

                        // Cancel any existing scheduled cleanup
                        if (cleanupTimeout.current) {
                            clearCleanupTimeout(cleanupTimeout.current);
                        }

                        // Schedule a new cleanup task after random delay
                        cleanupTimeout.current = setCleanupTimeout(() => {
                            cleanupTimeout.current = null;

                            const work = new CleanupWork({ dispatch, store, cleanup });

                            runCleanup(work);
                        }, 750 + Math.round(Math.random() * 150));
                    }
                };
            }
        }, [dispatch, store, dataSelector])
        return data;
    }, async (context, ...args) => {
        if (condition && !condition?.(...args.slice(0, condition.length))) {
            return defaultValue;
        }
        const hookArgs = condition?.length ? args.slice(condition.length) : args;
        const dataSelector = args.length === 0 ? selector : selector(...hookArgs);
        const initFunction = args.length === 0 ? init : init(...hookArgs);
        await realize(context, initFunction);
        // need to have the store so that if the redux updated above we get the state post-update
        return dataSelector(context.store.getState());
    }]
};

/**
 * This is a special dataHookMetaFactory for cases where the dataHook fetching needs to be guarded with a condition
 * check taking a single argument - credentials. Credentials is injected automatically so that the resulting data hook
 * can be used in the same way as a pure data hook factory - no need to useCredentials everywhere.
 * @param  {...any} args matching signature of dataHookFactory
 * @returns data hook
 */
export const dataHookWithCredentialsFactory = (...args) => {
    const [hook, realize] = dataHookFactory(...args);
    const useAuthHook = (...args) => {
        const credentials = useCredentials();
        return hook(credentials, ...args);
    }
    return [useAuthHook, (realizationContext, ...args) => realize(realizationContext, realizationContext.credentials, ...args)]
}

/**
 * This is a variation on a data hook which does NOT cause data to be fetched into the redux store - it relies
 * on another use of a data hook higher in the render hierarchy to be holding a given run function live. This liveness
 * is strictly validated with runtime errors for any case where the detects its associated run function (built by the init)
 * is not already live.
 * @param {*} selector A standard redux selector for the data to be returned by the generated hook
 * @param {*} init An init object or generator; an init object has a run method (to fetch data) and a listen method (to install listeners) which returns a cleanup function
 * @param {*} options A collection of options to the hook factory. The condition is a function which consumes the first n arguments
 * passed to the resulting data hook to produce a boolean value that gates data fetch. The default value is the value to be returned
 * by produced realize function is dependencies are not met.
 * @returns 
 */
export const readonlyHookFactory = (selector, init, { condition, defaultValue = [] } = {}) => {
    return [(...args) => {
        const hookArgs = useMemo(() => condition ? args.slice(condition.length) : args, [args]);
        const deps = condition?.(...(args.slice(0, condition.length))) ?? true;

        const dataSelector = useMemo(() => hookArgs.length === 0 ? selector : selector(...hookArgs), [...hookArgs])
        const data = useSelector(dataSelector);

        const initFunction = useMemo(() => {
            const i = hookArgs.length === 0 ? init : init(...hookArgs);
            let mergedDeps = deps;
            if (i.deps) {
                if (typeof i.deps === 'function') {
                    mergedDeps = (...args) => deps && i.deps(...args);
                } else if (typeof i.deps === 'boolean') {
                    mergedDeps = deps && i.deps;
                } else if (Array.isArray(i.deps)) {
                    mergedDeps = [...i.deps, deps];
                } else {
                    throw new Error("Don't know how to merge deps for type " + typeof i.deps);
                }
            }
            return { ...i, deps: mergedDeps };
        }, [...hookArgs, deps]);
        useReadonly(initFunction);

        return data;
    }, async ({ store }, ...args) => {
        if (!condition?.(...args.slice(0, condition.length))) {
            return defaultValue;
        }
        const hookArgs = condition?.length ? args.slice(condition.length) : args;
        const dataSelector = args.length === 0 ? selector : selector(...hookArgs);

        // need to have the store so that if the redux updated above we get the state post-update
        return dataSelector(store.getState());
    }]
}

/**
 * This is a special readonlyHookFactory for cases where the readonly hook needs to be guarded with a condition
 * check taking a single argument - credentials. Credentials is injected automatically so that the resulting data hook
 * can be used in the same way as a pure data hook factory - no need to useCredentials everywhere.
 * @param  {...any} args matching signature of dataHookFactory
 * @returns readonly hook
 */
export const readonlyHookWithCredentialsFactory = (...args) => {
    const [hook, realize] = readonlyHookFactory(...args);
    const useAuthHook = (...args) => {
        const credentials = useCredentials();
        return hook(credentials, ...args);
    }
    return [useAuthHook, (realizationContext, ...args) => realize(realizationContext, realizationContext.credentials, ...args)]
}

export const DefaultFailureAction = (verb, entity, action) => {
    const toReturn = (data, meta, tools) => {
        if (!['get', 'create', 'update', 'remove'].includes(verb)) {
            throw new Error('Unknown ');
        }
        if (data.error) {
            tools.showError(tools.t('defaultNetworkErrorMessage', { action: tools.t(verb + 'Network'), entity: tools.t(entity) }), data.error, { timestamp: data.timestap });
        } else {
            tools.showError(tools.t('defaultNetworkErrorMessage', { action: tools.t(verb), entity: tools.t(entity) }), tools.t('defaultNetworkErrorDetails', data), { timestamp: data.timestamp });
        }
        return action?.(data);
    };
    toReturn.hasAction = !!action;
    Object.defineProperty(toReturn, 'type', { get: () => action?.type });
    return toReturn;
}

export const DefaultSuccessAction = (verb, entity, action, dataExtractor = data => ({ name: data.name, id: data.id })) => {
    const toReturn = (data, meta, tools) => {
        let message;
        switch (verb) {
            case 'create':
                message = 'defaultNetworkCreateMessage';
                break;
            case 'update':
                message = 'defaultNetworkUpdateMessage';
                break;
            case 'remove':
                message = 'defaultNetworkRemoveMessage';
                break;
            default:
                throw new Error('Unknown network operation ' + verb);
        }
        tools.showSuccess(tools.t(message, { entity: tools.t(entity), ...dataExtractor(data) }));
        return action?.(data, meta);
    };
    toReturn.hasAction = !!action;
    Object.defineProperty(toReturn, 'type', { get: () => action?.type });
    return toReturn;
}

/**
 * This hook builds the context object required by realization functions to operate correctly.
 */
export const useRealizationContext = () => {
    const { t } = useTranslation();
    const store = useStore();
    const network = useNetwork();
    const websocket = useWebsocket();
    const clientConfig = useClientConfig();
    const credentials = useCredentials();

    return useMemo(() => ({ t, store, network, websocket, clientConfig, credentials }), [t, store, network, websocket, clientConfig, credentials]);
}

/**
 * This is a hook factory which will run the hooks supplied in parallel and return the result produced first one
 * (ie lowest index in the supplied hooks array) which is truthy.
 * 
 * Note: this hook does NOT short circuit and so when combined with dataHookFactory could result in multiple parallel fetch
 */
export const orderedHookFactory = (...hooks) => (...args) => {
    const results = hooks.map(hook => hook(...args));

    return useMemo(() => results.find(r => r && Array.isArray(r) ? r.length > 0 : Boolean(r)) ?? results[0], [...results]);
}

/**
 * This is a hook factory which will run the hooks supplied in parallel and return the distinct union of the results.
 * 
 * Note: this hook does NOT short circuit and so will result in multiple parallel fetch
 * 
 * @param {*} pkey The primary key used to distinct the results (undefined will distinct on element === identity)
 * @param  {...any} hooks The hooks to run
 * @returns The union result of the hooks
 */
export const unifyingHookFactory = (pkey, ...hooks) => (...args) => {
    const results = hooks.map(hook => hook(...args));

    return useMemo(() => distinct(results.flat(), pkey), [...results, pkey]);
}
